import React, { useState } from 'react';
import { HighlightWithPlayer } from '../../types/highlights.types';
import { FaTimes, FaTwitter, FaLink, FaDiscord, FaCheck } from 'react-icons/fa';
import highlightsService from '../../services/highlightsService';
import '../../styles/components/highlights.scss';

interface ShareMenuProps {
  highlight: HighlightWithPlayer;
  matchId: string;
  onClose: () => void;
}

const ShareMenu: React.FC<ShareMenuProps> = ({ highlight, matchId, onClose }) => {
  const [copied, setCopied] = useState(false);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleTwitterShare = () => {
    const url = highlightsService.getTwitterShareUrl(
      highlight.clipUrl,
      matchId,
      highlight.playerName,
      highlight.killCount
    );
    window.open(url, '_blank', 'width=550,height=420');
  };

  const handleCopyLink = async () => {
    const success = await highlightsService.copyToClipboard(highlight.clipUrl);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDiscordShare = async () => {
    // For Discord, we'll just copy the URL with a formatted message
    const message = `Check out this ${highlight.killCount}K highlight${
      highlight.playerName ? ` by ${highlight.playerName}` : ''
    }!\n${highlight.clipUrl}`;

    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Error copying Discord message:', error);
    }
  };

  return (
    <div className="share-menu" onClick={handleBackdropClick} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleBackdropClick(e as any); }}>
      <div className="share-menu__container">
        <div className="share-menu__header">
          <h3 className="share-menu__title">Share Highlight</h3>
          <button
            className="share-menu__close"
            onClick={onClose}
            aria-label="Close share menu"
          >
            <FaTimes />
          </button>
        </div>

        <div className="share-menu__content">
          <div className="share-menu__info">
            <span className="share-menu__player">
              {highlight.playerName || 'Unknown Player'}
            </span>
            <span className="share-menu__details">
              Round {highlight.roundNumber} • {highlight.killCount} Kill
              {highlight.killCount !== 1 ? 's' : ''}
            </span>
          </div>

          <div className="share-menu__options">
            <button
              className="share-menu__option"
              onClick={handleTwitterShare}
            >
              <FaTwitter className="share-menu__icon share-menu__icon--twitter" />
              <span>Share on Twitter</span>
            </button>

            <button
              className="share-menu__option"
              onClick={handleDiscordShare}
            >
              <FaDiscord className="share-menu__icon share-menu__icon--discord" />
              <span>Copy for Discord</span>
            </button>

            <button
              className="share-menu__option"
              onClick={handleCopyLink}
            >
              {copied ? (
                <>
                  <FaCheck className="share-menu__icon share-menu__icon--success" />
                  <span>Copied!</span>
                </>
              ) : (
                <>
                  <FaLink className="share-menu__icon" />
                  <span>Copy Direct Link</span>
                </>
              )}
            </button>
          </div>

          <div className="share-menu__url">
            <input
              type="text"
              className="share-menu__url-input"
              value={highlight.clipUrl}
              readOnly
              onClick={(e) => e.currentTarget.select()}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareMenu;
